/**
 * Panier v2 — bloc « Code promo / Carte cadeau » en accordéon, AU-DESSUS de « Moyen de paiement » (retour de Luc du
 * 08/10 : « Avantages » au-dessus du moyen de paiement, titre plus explicite, dépliable ; SPEC § 4.7, CK1).
 *
 * Mise en page SEULEMENT : le contenu est le bloc Avantages du Nouveau Panier (screens/Checkout/v2/Advantages.tsx),
 * passé tel quel en enfant par CheckoutFormV2 (option `AdvantagesFrame` de screens/Checkout/v2/layout.ts, fournie par
 * CheckoutV2) : mêmes champs, mêmes appels (/sale/promocode, /sale/giftcard), même état react-hook-form. Ce composant
 * ne fait que LIRE ce que ce bloc écrit (code, remise, carte, solde, erreur du code) pour le résumé de l'en-tête et
 * l'ouverture d'office (advantages.ts).
 *
 * - Fermé par défaut ; ouvert d'office si un avantage est appliqué ou si une erreur est à afficher.
 * - En-tête = bouton sur toute la largeur (aria-expanded, aria-controls), dans un h3 (motif accordéon de l'APG).
 * - Titre « Code promo / Carte cadeau », ou « Code promo » quand le bloc ne propose pas la carte cadeau (réglage BO
 *   « Cartes cadeaux » restreint, pack hors liste : giftCardOffered, la règle du bloc lui-même).
 * - Ouverture au clic : le premier champ du bloc reçoit le focus (dans le geste : le clavier s'ouvre sur téléphone ;
 *   mouvement réduit compris, voir checkout.css). Fermeture avec le focus dans le panneau : focus rendu à l'en-tête.
 * - Hauteur animée sans mesure (grille 0fr → 1fr) ; le panneau fermé est masqué (visibility) : ni lu, ni atteint au
 *   clavier ; ce qui est saisi est gardé. Mouvement réduit : sans animation (checkout.css et styles/ux-v2.css).
 * - Erreurs du code promo annoncées (role="alert"), avantage appliqué annoncé (role="status") : le message visible du
 *   Nouveau Panier n'est pas une région annoncée.
 */
import { VisuallyHidden } from '@chakra-ui/react';
import { useEffect, useId, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useFormState, useWatch } from 'react-hook-form';
import type { AdvantagesFrameProps } from 'screens/Checkout/v2/layout';
import type { CheckoutData } from 'screens/Checkout/useCheckoutForm';
import { advantagesSummary, giftCardOffered, shouldOpenAdvantages } from './advantages';

/** Icônes au trait de 1,8 en currentColor, comme celles de la maquette /msc ; décoratives. */
function Glyph({ d, size = 18 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d={d} />
    </svg>
  );
}

/** Étiquette de prix (tracé de Lucide « tag », licence ISC) et son œillet. */
const TAG = 'M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42zM7.5 7.5h.01';
const CHECK = 'M20 6 9 17l-5-5';
const CHEVRON = 'm6 9 6 6 6-6';

/** Titre quand le bloc propose la carte cadeau ; sinon (réglage BO « Cartes cadeaux » restreint, pack hors liste),
 *  le bloc n'a que le code promo et le titre ne promet rien d'autre. */
export const ADVANTAGES_TITLE = 'Code promo / Carte cadeau';
export const ADVANTAGES_TITLE_PROMO_ONLY = 'Code promo';
export const ADVANTAGES_HINT = 'Déduit du total avant le paiement';

export function AdvantagesAccordion({ product, children }: AdvantagesFrameProps) {
  // Même règle que le bloc Avantages qu'il contient (carte cadeau rendue ou non).
  const title = giftCardOffered(product, mondayMindbody?.giftCardConfig) ? ADVANTAGES_TITLE : ADVANTAGES_TITLE_PROMO_ONLY;
  const [promoCode, discount, giftCard, giftCardBalance, totalAmount] = useWatch<CheckoutData, ['PromoCode', 'DiscountedAmount', 'GiftCard', 'GiftCardBalance', 'TotalAmount']>({
    name: ['PromoCode', 'DiscountedAmount', 'GiftCard', 'GiftCardBalance', 'TotalAmount'],
  });
  const { errors } = useFormState<CheckoutData>({ name: 'PromoCode' });
  const error = typeof errors.PromoCode?.message === 'string' && errors.PromoCode.message ? errors.PromoCode.message : null;

  const summary = advantagesSummary({ promoCode, discount, giftCard, giftCardBalance, totalAmount });
  const applied = summary !== null;

  const [open, setOpen] = useState(() => shouldOpenAdvantages({ applied, error: !!error }));
  // Un avantage qui arrive (déjà appliqué) ou une nouvelle erreur rouvrent le bloc ; le client peut ensuite le refermer.
  useEffect(() => {
    if (shouldOpenAdvantages({ applied, error: !!error })) setOpen(true);
  }, [applied, error]);

  const id = useId();
  const titleId = `${id}-title`;
  const subId = `${id}-sub`;
  const panelId = `${id}-panel`;
  const panelRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLButtonElement>(null);

  // Safari ne donne pas le focus au bouton cliqué : dès l'appui, il le retire du champ (vers `main` ou body), avant le
  // clic. Où était le focus est donc relevé à l'appui (pointerdown, souris comme doigt).
  const focusInPanelOnPress = useRef(false);
  const notePress = () => {
    focusInPanelOnPress.current = !!panelRef.current?.contains(document.activeElement);
  };

  const toggle = () => {
    const focusWasInPanel = focusInPanelOnPress.current || !!panelRef.current?.contains(document.activeElement);
    focusInPanelOnPress.current = false;
    if (open) {
      // Focus dans le panneau qui se ferme : rendu à l'en-tête, pour ne le perdre ni dans un champ masqué ni sur la page.
      if (focusWasInPanel) headRef.current?.focus({ preventScroll: true });
      setOpen(false);
      return;
    }
    // Rendu synchrone : le panneau est visible (visibility) avant le focus, toujours dans le geste du client.
    flushSync(() => setOpen(true));
    panelRef.current?.querySelector<HTMLInputElement>('input:not([disabled])')?.focus({ preventScroll: true });
  };

  return (
    <section className={`mmv2-ck-adv${open ? ' mmv2-ck-adv--open' : ''}${applied ? ' mmv2-ck-adv--applied' : ''}`} data-open={open ? 'true' : 'false'}>
      <h3 className="mmv2-ck-adv__heading">
        <button ref={headRef} type="button" className="mmv2-ck-adv__head" aria-expanded={open} aria-controls={panelId} aria-labelledby={titleId} aria-describedby={subId} onPointerDown={notePress} onClick={toggle}>
          <span className="mmv2-ck-adv__icon" aria-hidden="true">
            <Glyph d={applied ? CHECK : TAG} />
          </span>
          <span className="mmv2-ck-adv__text">
            <span className="mmv2-ck-adv__title" id={titleId}>
              {title}
            </span>
            <span className="mmv2-ck-adv__sub" id={subId}>
              {summary ?? ADVANTAGES_HINT}
            </span>
          </span>
          <span className="mmv2-ck-adv__chevron" aria-hidden="true">
            <Glyph d={CHEVRON} size={16} />
          </span>
        </button>
      </h3>
      <div className="mmv2-ck-adv__panel" id={panelId} role="region" aria-labelledby={titleId} ref={panelRef}>
        <div className="mmv2-ck-adv__clip">
          <div className="mmv2-ck-adv__body">{children}</div>
        </div>
      </div>
      <VisuallyHidden as="div" role="status">
        {summary ? `Avantage appliqué : ${summary}` : ''}
      </VisuallyHidden>
      <VisuallyHidden as="div" role="alert">
        {error ?? ''}
      </VisuallyHidden>
    </section>
  );
}
